import { useState, useEffect, useCallback, ChangeEvent } from "react";
import {
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiSave,
  FiX,
  FiImage,
  FiLink as FiLinkIcon,
  FiInfo,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import advertisements, { Advertisement } from "../../services/advertisements";
import blobService from "../../services/blob";
import utils from "../../helpers/utils";
import Chart from "../charts/Chart";
import { ChartData } from "../../types";
import MonthlySalesChart from "../../tailadmin/components/ecommerce/MonthlySalesChart";

export default function AdvertisementTab() {
  const { getUser } = useAuth();
  const [ads, setAds] = useState<Advertisement[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<{
    imageUri: string;
    link: string;
    active?: boolean;
  }>({
    imageUri: "",
    link: "",
    active: true,
  });
  const [imagePreview, setImagePreview] = useState<string>("");
  const [stats, setStats] = useState<{
    clicks_by_day: ChartData[];
    clicks_by_advertisement: {
      advertisement_id: number;
      link: string;
      count: number;
    }[];
    views_by_day: ChartData[];
    views_by_advertisement: {
      advertisement_id: number;
      link: string;
      count: number;
    }[];
  } | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState<string | null>(null);

  const loadAds = useCallback(async () => {
    const user = getUser();
    if (!user?.id) return;

    setLoading(true);
    try {
      const data = await advertisements.listProfessionalAds(String(user.id));
      setAds(data || []);
    } catch (error) {
      console.error("Erro ao carregar anúncios:", error);
      toast.error("Erro ao carregar anúncios");
    } finally {
      setLoading(false);
    }
  }, [getUser]);

  const loadStats = useCallback(async () => {
    const user = getUser();
    if (!user?.id) return;

    try {
      const data = await advertisements.getStats(String(user.id), 30);
      if (data) {
        setStats({
          clicks_by_day: (data.clicks_by_day || []).map((item) => ({
            date: item.date,
            count: item.count,
          })),
          clicks_by_advertisement: data.clicks_by_advertisement || [],
          views_by_day: (data.views_by_day || []).map((item) => ({
            date: item.date,
            count: item.count,
          })),
          views_by_advertisement: data.views_by_advertisement || [],
        });
      }
    } catch (error) {
      console.error("Erro ao carregar estatísticas:", error);
    }
  }, [getUser]);

  useEffect(() => {
    loadAds();
    loadStats();
  }, [loadAds, loadStats]);

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const user = getUser();
    if (!user?.id) {
      toast.error("Usuário não encontrado");
      return;
    }

    try {
      const base64String = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          resolve(result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const base64Data = base64String.split(",")[1];
      const uploadResult = await blobService.uploadImage(
        String(user.id),
        base64Data,
        file.name,
      );

      if (uploadResult) {
        let imagePath: string;
        if (typeof uploadResult === "object" && uploadResult.url) {
          imagePath = uploadResult.url;
        } else if (typeof uploadResult === "object" && uploadResult.path) {
          imagePath = uploadResult.path;
        } else if (typeof uploadResult === "string") {
          imagePath = `/${user.id}/${uploadResult}`;
        } else {
          imagePath = `/${user.id}/${uploadResult.file_name || ""}`;
        }

        setFormData({ ...formData, imageUri: imagePath });
        setImagePreview(utils.getUriImage(imagePath));
        toast.success("Imagem carregada com sucesso!");
      } else {
        toast.error("Erro ao fazer upload da imagem");
      }
    } catch (error) {
      console.error("Erro ao processar imagem:", error);
      toast.error("Erro ao processar imagem");
    }
  };

  const handleSave = async () => {
    if (!formData.imageUri || !formData.link) {
      toast.error("Imagem e link são obrigatórios");
      return;
    }

    const user = getUser();
    if (!user?.id) return;

    setSaving(true);
    try {
      if (editingId) {
        const result = await advertisements.updateAdvertisement(
          editingId,
          formData,
        );
        if (result) {
          toast.success("Anúncio atualizado com sucesso!");
          await loadAds();
          await loadStats();
          resetForm();
        } else {
          toast.error("Erro ao atualizar anúncio");
        }
      } else {
        const result = await advertisements.createAdvertisement(
          String(user.id),
          formData,
        );
        if (result) {
          toast.success("Anúncio criado com sucesso!");
          await loadAds();
          await loadStats();
          resetForm();
        } else {
          toast.error("Erro ao criar anúncio");
        }
      }
    } catch (error) {
      console.error("Erro ao salvar anúncio:", error);
      toast.error("Erro ao salvar anúncio");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (ad: Advertisement) => {
    setEditingId(ad.id);
    setFormData({
      imageUri: ad.imageUri,
      link: ad.link,
      active: ad.active ?? true,
    });
    setImagePreview(utils.getUriImage(ad.imageUri));
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Tem certeza que deseja deletar este anúncio?")) {
      return;
    }

    try {
      const success = await advertisements.deleteAdvertisement(id);
      if (success) {
        toast.success("Anúncio deletado com sucesso!");
        await loadAds();
        await loadStats();
      } else {
        toast.error("Erro ao deletar anúncio");
      }
    } catch (error) {
      console.error("Erro ao deletar anúncio:", error);
      toast.error("Erro ao deletar anúncio");
    }
  };

  const resetForm = () => {
    setFormData({ imageUri: "", link: "", active: true });
    setImagePreview("");
    setEditingId(null);
    setShowForm(false);
  };

  const InfoTooltip = ({ id, text }: { id: string; text: string }) => (
    <div className="relative inline-block">
      <button
        onMouseEnter={() => setTooltipVisible(id)}
        onMouseLeave={() => setTooltipVisible(null)}
        onClick={() => setTooltipVisible(tooltipVisible === id ? null : id)}
        className="ml-2 inline-flex items-center justify-center rounded-full text-gray-400 transition hover:text-purple-600 dark:hover:text-purple-400"
      >
        <FiInfo size={16} />
      </button>
      {tooltipVisible === id && (
        <div className="absolute left-0 top-6 z-50 w-64 rounded-lg border border-stroke bg-white p-3 text-xs leading-relaxed text-gray-600 shadow-lg dark:border-strokedark dark:bg-boxdark dark:text-gray-300">
          {text}
          <div className="absolute -top-1 left-4 h-2 w-2 rotate-45 border-l border-t border-stroke bg-white dark:border-strokedark dark:bg-boxdark" />
        </div>
      )}
    </div>
  );

  // Preparar dados para o gráfico de cliques por anúncio
  const categoriesByAd =
    stats?.clicks_by_advertisement.map((item) => {
      try {
        const url = new URL(item.link);
        const domain = url.hostname.replace("www.", "");
        return `Anúncio #${item.advertisement_id} (${domain})`;
      } catch {
        const truncated =
          item.link.length > 30
            ? `${item.link.substring(0, 30)}...`
            : item.link;
        return `Anúncio #${item.advertisement_id} - ${truncated}`;
      }
    }) || [];
  const dataByAd =
    stats?.clicks_by_advertisement.map((item) => item.count) || [];

  // Preparar dados para o gráfico de visualizações por anúncio
  const categoriesByView =
    stats?.views_by_advertisement.map((item) => {
      try {
        const url = new URL(item.link);
        const domain = url.hostname.replace("www.", "");
        return `Anúncio #${item.advertisement_id} (${domain})`;
      } catch {
        const truncated =
          item.link.length > 30
            ? `${item.link.substring(0, 30)}...`
            : item.link;
        return `Anúncio #${item.advertisement_id} - ${truncated}`;
      }
    }) || [];
  const dataByView =
    stats?.views_by_advertisement.map((item) => item.count) || [];

  const viewsByDay = stats?.views_by_day || [];
  const totalViewsLast30Days = viewsByDay.reduce(
    (sum, item) => sum + item.count,
    0,
  );
  const averageViewsPerDay =
    viewsByDay.length > 0
      ? Math.round(totalViewsLast30Days / viewsByDay.length)
      : 0;
  const bestDayViews =
    viewsByDay.length > 0
      ? Math.max(...viewsByDay.map((item) => item.count))
      : 0;

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header com botão de adicionar */}
      <div className="flex flex-col gap-3 sm:gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex-1">
          <h3 className="text-lg font-bold text-black dark:text-white sm:text-xl">
            Meus Anúncios
          </h3>
          <p className="mt-1.5 max-w-3xl text-xs leading-relaxed text-gray-600 dark:text-gray-400 sm:mt-2 sm:text-sm">
            Gerencie os anúncios que serão exibidos no app dos seus alunos e
            acompanhe as estatísticas de cliques em cada um deles.
          </p>
          <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-500 sm:text-xs">
            Recomendamos imagens retangulares com proporção 16:9
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-purple-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 sm:w-auto sm:px-5"
          >
            <FiPlus size={16} className="sm:w-[18px] sm:h-[18px]" />
            Novo Anúncio
          </button>
        )}
      </div>

      {/* Formulário */}
      {showForm && (
        <div className="rounded-lg border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark sm:rounded-xl">
          <div className="flex items-center justify-between border-b border-stroke p-4 dark:border-strokedark sm:p-6">
            <h4 className="text-base font-semibold text-black dark:text-white sm:text-lg">
              {editingId ? "Editar Anúncio" : "Novo Anúncio"}
            </h4>
            <button
              onClick={resetForm}
              className="rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-300 sm:p-2"
            >
              <FiX size={18} className="sm:w-5 sm:h-5" />
            </button>
          </div>

          <div className="p-4 space-y-6 sm:p-6 sm:space-y-8">
            {/* Upload de Imagem */}
            <div>
              <label className="mb-3 block text-sm font-semibold text-black dark:text-white sm:mb-4 sm:text-base">
                Imagem do Anúncio
              </label>

              <div className="flex flex-col items-center gap-4 sm:gap-6">
                <div className="w-full max-w-2xl">
                  {imagePreview ? (
                    <div className="overflow-hidden rounded-lg border-2 border-purple-600 shadow-lg dark:border-purple-500 sm:rounded-xl">
                      <img
                        src={imagePreview}
                        alt="Preview do anúncio"
                        className="w-full h-auto aspect-video object-cover"
                      />
                    </div>
                  ) : (
                    <div className="flex aspect-video w-full items-center justify-center rounded-lg border-2 border-dashed border-purple-300 bg-gradient-to-br from-purple-50 to-purple-100 dark:border-purple-700 dark:from-gray-800 dark:to-gray-900 sm:rounded-xl">
                      <div className="flex flex-col items-center gap-2.5 text-center sm:gap-3">
                        <FiImage
                          className="text-purple-400 dark:text-purple-500"
                          size={48}
                        />
                        <p className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                          Nenhuma imagem selecionada
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <label
                  htmlFor="ad-image-upload"
                  className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-purple-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-purple-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:bg-purple-500 dark:hover:bg-purple-600 sm:gap-2.5 sm:px-6 sm:py-3"
                >
                  <FiImage size={18} className="sm:w-5 sm:h-5" />
                  {imagePreview ? "Alterar Imagem" : "Selecionar Imagem"}
                </label>
                <input
                  id="ad-image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </div>
            </div>

            {/* Link */}
            <div>
              <label className="mb-2 block text-xs font-semibold text-black dark:text-white sm:mb-2.5 sm:text-sm">
                Link de Destino
              </label>
              <div className="relative">
                <FiLinkIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 sm:left-4"
                  size={16}
                />
                <input
                  type="url"
                  value={formData.link}
                  onChange={(e) =>
                    setFormData({ ...formData, link: e.target.value })
                  }
                  placeholder="https://exemplo.com"
                  className="w-full rounded-lg border border-stroke bg-transparent py-2.5 pl-10 pr-3 text-xs text-black outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:text-white dark:focus:border-purple-500 sm:py-3 sm:pl-12 sm:pr-4 sm:text-sm"
                />
              </div>
            </div>

            {/* Botões */}
            <div className="flex flex-col gap-2.5 pt-2 sm:flex-row sm:flex-wrap sm:gap-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-purple-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:px-6"
              >
                <FiSave size={14} className="sm:w-4 sm:h-4" />
                {saving ? "Salvando..." : "Salvar Anúncio"}
              </button>
              <button
                onClick={resetForm}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-stroke bg-white px-5 py-2.5 text-sm font-semibold text-black shadow-sm transition-all hover:bg-gray-50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 dark:border-strokedark dark:bg-boxdark dark:text-white dark:hover:bg-gray-800 sm:px-6"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Estado vazio */}
      {!loading && ads.length === 0 && !showForm && (
        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-10 text-center dark:border-gray-700 dark:bg-gray-800/50 sm:rounded-xl sm:p-16">
          <FiImage className="mx-auto mb-3 text-gray-400 sm:mb-4" size={40} />
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 sm:text-base">
            Nenhum anúncio cadastrado
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500 sm:text-sm">
            Clique em "Novo Anúncio" para começar
          </p>
        </div>
      )}

      {/* Lista de Anúncios */}
      {!loading && ads.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
          {ads.map((ad) => (
            <div
              key={ad.id}
              className="group overflow-hidden rounded-lg border border-stroke bg-white shadow-sm transition-all hover:shadow-md dark:border-strokedark dark:bg-boxdark sm:rounded-xl"
            >
              <div className="aspect-video overflow-hidden">
                <img
                  src={utils.getUriImage(ad.imageUri)}
                  alt={ad.link}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="p-4 space-y-3 sm:p-5 sm:space-y-4">
                <a
                  href={ad.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block truncate text-xs font-medium text-purple-600 transition hover:text-purple-700 hover:underline dark:text-purple-400 dark:hover:text-purple-300 sm:text-sm"
                  title={ad.link}
                >
                  {ad.link}
                </a>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(ad)}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-purple-300 bg-purple-50 px-3 py-2 text-xs font-semibold text-purple-700 transition-all hover:bg-purple-100 hover:border-purple-400 dark:border-purple-700 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50 sm:gap-2 sm:px-4 sm:py-2.5 sm:text-sm"
                  >
                    <FiEdit2 size={12} className="sm:w-[14px] sm:h-[14px]" />
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(ad.id)}
                    className="flex items-center justify-center rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 transition-all hover:bg-red-100 hover:border-red-400 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 sm:px-4 sm:py-2.5 sm:text-sm"
                  >
                    <FiTrash2 size={12} className="sm:w-[14px] sm:h-[14px]" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Gráficos de Estatísticas */}
      {stats && (
        <div className="mt-8 space-y-4 sm:mt-10 sm:space-y-6">
          <div className="space-y-2 sm:space-y-3">
            <div className="border-t border-dashed border-stroke dark:border-strokedark" />
            <h3 className="text-base font-semibold text-black dark:text-white sm:text-lg">
              Gráficos de Estatísticas
            </h3>
          </div>
          {/* Gráfico de Visualizações por Dia */}
          <div className="rounded-lg border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark sm:rounded-xl">
            <div className="border-b border-stroke p-4 dark:border-strokedark sm:p-6">
              <div className="flex items-center">
                <h4 className="text-base font-semibold text-black dark:text-white sm:text-lg">
                  Visualizações por Dia
                </h4>
                <InfoTooltip
                  id="views-day"
                  text="Mostra quantas vezes seus anúncios foram visualizados por dia nos últimos 30 dias. Cada vez que o anúncio aparece na tela de um aluno, é contada uma visualização."
                />
              </div>
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                Acompanhe o alcance diário dos seus anúncios
              </p>
            </div>
            <div className="p-4 space-y-4 sm:p-6 sm:space-y-6">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
                <div className="rounded-lg bg-gradient-to-br from-purple-50 to-purple-100 p-4 dark:from-purple-900/20 dark:to-purple-800/20 sm:rounded-xl sm:p-5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 sm:text-xs">
                    Total no Período
                  </p>
                  <p className="mt-2 text-2xl font-bold text-purple-700 dark:text-purple-300 sm:mt-3 sm:text-3xl">
                    {totalViewsLast30Days.toLocaleString("pt-BR")}
                  </p>
                </div>
                <div className="rounded-lg bg-gradient-to-br from-sky-50 to-sky-100 p-4 dark:from-sky-900/20 dark:to-sky-800/20 sm:rounded-xl sm:p-5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 sm:text-xs">
                    Média por Dia
                  </p>
                  <p className="mt-2 text-2xl font-bold text-sky-700 dark:text-sky-300 sm:mt-3 sm:text-3xl">
                    {averageViewsPerDay.toLocaleString("pt-BR")}
                  </p>
                </div>
                <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-emerald-100 p-4 dark:from-emerald-900/20 dark:to-emerald-800/20 sm:rounded-xl sm:p-5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 sm:text-xs">
                    Pico em um Dia
                  </p>
                  <p className="mt-2 text-2xl font-bold text-emerald-700 dark:text-emerald-300 sm:mt-3 sm:text-3xl">
                    {bestDayViews.toLocaleString("pt-BR")}
                  </p>
                </div>
              </div>
              <div className="pt-2">
                <Chart data={stats.views_by_day} sufix=" visualizações" />
              </div>
            </div>
          </div>

          {/* Gráfico de Visualizações por Anúncio */}
          <div className="rounded-xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark">
            <div className="border-b border-stroke p-6 dark:border-strokedark">
              <div className="flex items-center">
                <h4 className="text-lg font-semibold text-black dark:text-white">
                  Visualizações por Anúncio
                </h4>
                <InfoTooltip
                  id="views-ad"
                  text="Compara o desempenho de cada anúncio individualmente. Use esses dados para identificar quais anúncios têm mais visibilidade e ajustar sua estratégia."
                />
              </div>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Compare o desempenho de cada anúncio
              </p>
            </div>
            <div className="p-6">
              {stats.views_by_advertisement.length > 0 ? (
                <MonthlySalesChart
                  title=""
                  data={dataByView}
                  categories={categoriesByView}
                />
              ) : (
                <div className="flex h-64 items-center justify-center">
                  <div className="text-center">
                    <FiImage className="mx-auto mb-3 text-gray-400" size={40} />
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Nenhuma visualização registrada
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Gráfico de Cliques por Dia */}
          <div className="rounded-xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark">
            <div className="border-b border-stroke p-6 dark:border-strokedark">
              <div className="flex items-center">
                <h4 className="text-lg font-semibold text-black dark:text-white">
                  Cliques por Dia
                </h4>
                <InfoTooltip
                  id="clicks-day"
                  text="Registra quando seus alunos clicam nos anúncios por dia. Cliques indicam engajamento real - o aluno teve interesse suficiente para acessar o link do anúncio."
                />
              </div>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Monitore o engajamento diário com seus anúncios
              </p>
            </div>
            <div className="p-6 pt-8">
              <Chart data={stats.clicks_by_day} sufix=" cliques" />
            </div>
          </div>

          {/* Gráfico de Cliques por Anúncio */}
          <div className="rounded-xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark">
            <div className="border-b border-stroke p-6 dark:border-strokedark">
              <div className="flex items-center">
                <h4 className="text-lg font-semibold text-black dark:text-white">
                  Cliques por Anúncio
                </h4>
                <InfoTooltip
                  id="clicks-ad"
                  text="Identifica quais anúncios geram mais cliques. Uma taxa de clique alta (cliques/visualizações) indica que o anúncio é relevante e atraente para seus alunos."
                />
              </div>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Identifique seus anúncios mais efetivos
              </p>
            </div>
            <div className="p-6">
              {stats.clicks_by_advertisement.length > 0 ? (
                <MonthlySalesChart
                  title=""
                  data={dataByAd}
                  categories={categoriesByAd}
                />
              ) : (
                <div className="flex h-64 items-center justify-center">
                  <div className="text-center">
                    <FiImage className="mx-auto mb-3 text-gray-400" size={40} />
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Nenhum clique registrado
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
